import { ClinicDayHours, ClinicEducationalResource, DAYS_OF_WEEK, DayOfWeek } from "@/types/clinic-types";
import { COUNTRY_DEFAULTS } from "@/utils/country-defaults";
import { formatters } from "@/utils/fhir-utils";
import { DEFAULT_CLINIC_TIMEZONE } from "@/utils/timezones";

import { fhirR4 } from "@smile-cdr/fhirts";
import dayjs, { Dayjs } from "dayjs";

// Extension URLs
// TODO (VMD-877): these URLs and the sub-extension names are a cross-repo
// contract duplicated in the patient app and Engage. They belong in
// @vitallhealth/fhir-core alongside FHIR_SYSTEMS, so every repo imports one
// definition instead of keeping its own copy in sync.
export const PATIENT_CLINIC_EXTENSION_URL = "http://vitall.com/fhir/ext/patient-clinic";
export const PRIMARY_CONTACT_EXTENSION_URL = "http://vitall.com/fhir/ext/primary-contact-practitioner";
export const CLINIC_TIMEZONE_EXTENSION_URL = "https://vitall.com/fhir/ext/clinic-timezone";
// Links the Organization to its contained Location (which holds hours + timezone).
export const CLINIC_LOCATION_EXTENSION_URL = "https://vitall.com/fhir/ext/clinic-location";
// On the contained Location, next to clinic-timezone. Read by the patient app (VMA-246):
// the URLs and sub-extension names are a cross-repo contract.
export const CLINIC_EMERGENCY_NUMBER_EXTENSION_URL = "https://vitall.com/fhir/ext/clinic-emergency-number";
export const CLINIC_EDUCATIONAL_RESOURCE_EXTENSION_URL = "https://vitall.com/fhir/ext/clinic-educational-resource";

// id of the contained Location resource on the clinic Organization.
export const CONTAINED_LOCATION_ID = "clinic-location";

// The number patients are told to call when no clinic-specific one applies —
// the patient app's historical hardcoded fallback.
export const DEFAULT_EMERGENCY_NUMBER = "911";

/** Emergency number for a country code, or "" when the country has no mapping. */
export const getAutoEmergencyNumber = (countryCode: string): string => {
  return COUNTRY_DEFAULTS[countryCode]?.emergencyNumber ?? "";
};

/**
 * Educational resources pre-filled for a country, or none when it has no
 * default. Returns a list because a clinic stores a list, though the form
 * currently edits one.
 */
export const getDefaultEducationalResources = (countryCode: string): ClinicEducationalResource[] => {
  const resource = COUNTRY_DEFAULTS[countryCode]?.educationalResource;
  return resource ? [resource] : [];
};

/** Emergency numbers are digits only, 2–4 characters (France's SAMU is 15). */
export const isValidEmergencyNumber = (value: string): boolean => {
  return /^\d{2,4}$/.test(value);
};

/**
 * Educational resource links must be absolute http(s) URLs with no whitespace:
 * the value is stored as a FHIR `url`, whose type forbids whitespace (and
 * `new URL` would otherwise silently accept padded or spaced input).
 */
export const isValidResourceUrl = (value: string): boolean => {
  if (/\s/.test(value)) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

// Default operating hours: Mon–Fri 08:00–16:00 open, weekends closed.
const DEFAULT_OPEN_START = "08:00";
const DEFAULT_OPEN_END = "16:00";
const WEEKDAYS: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri"];

/**
 * Default clinic hours used for new clinics and as the fallback when an
 * existing clinic has no stored hours (backfill): Mon–Fri 08:00–16:00 open,
 * Saturday and Sunday closed.
 */
export const getDefaultClinicHours = (): ClinicDayHours[] => {
  return DAYS_OF_WEEK.map(day => ({
    day,
    open: WEEKDAYS.includes(day),
    start: DEFAULT_OPEN_START,
    end: DEFAULT_OPEN_END,
  }));
};

// --- Clinic-hours time conversions -----------------------------------------
// The form represents a time as a 24h "HH:mm" string. These helpers convert
// between that, the FHIR `time` ("HH:mm:ss") stored on the resource, and the
// Dayjs value the MUI TimePicker works with.

/** Converts a "HH:mm" form value to a FHIR `time` ("HH:mm:ss"). */
export const toFhirTime = (value: string): string => {
  return /^\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
};

/** Converts a FHIR `time` ("HH:mm:ss") back to a "HH:mm" form value. */
export const fromFhirTime = (value: string | undefined): string => {
  if (!value) {
    return DEFAULT_OPEN_START;
  }
  const match = value.match(/^(\d{2}:\d{2})/);
  return match ? match[1] : value;
};

/** Parses a "HH:mm" form value into a Dayjs (on a fixed date) for the TimePicker. */
export const toDayjsTime = (value: string): Dayjs | null => {
  const parsed = dayjs(`2000-01-01 ${value}`);
  return parsed.isValid() ? parsed : null;
};

/** Formats a Dayjs from the TimePicker back to a "HH:mm" form value. */
export const fromDayjsTime = (value: Dayjs | null): string => {
  return value && value.isValid() ? value.format("HH:mm") : "";
};

/**
 * Builds the clinic-timezone extension (stored on the contained Location).
 */
export const buildClinicTimezoneExtension = (timezone: string): fhirR4.Extension => {
  return {
    url: CLINIC_TIMEZONE_EXTENSION_URL,
    valueString: timezone,
  };
};

/**
 * Converts the per-day form hours into FHIR `Location.hoursOfOperation` —
 * one entry per open day. Closed days are simply omitted (FHIR convention).
 */
export const buildClinicHoursOfOperation = (hours: ClinicDayHours[]): fhirR4.LocationHoursOfOperation[] => {
  return hours
    .filter(dayHours => dayHours.open)
    .map(dayHours => ({
      daysOfWeek: [dayHours.day],
      openingTime: toFhirTime(dayHours.start),
      closingTime: toFhirTime(dayHours.end),
    }));
};

export interface BuildClinicLocationParams {
  timezone: string;
  hours: ClinicDayHours[];
  /** ISO 3166-1 alpha-2 code; empty/absent stores no address. */
  country?: string;
  /** Digits only, 2–4 characters; empty/absent stores no extension. */
  emergencyNumber?: string;
  /** Empty/absent stores no extensions. */
  educationalResources?: ClinicEducationalResource[];
}

const buildEducationalResourceExtension = (resource: ClinicEducationalResource): fhirR4.Extension => {
  return {
    url: CLINIC_EDUCATIONAL_RESOURCE_EXTENSION_URL,
    extension: [
      { url: "label", valueString: resource.label },
      { url: "url", valueUrl: resource.url },
    ],
  };
};

/**
 * Builds the contained Location resource holding the clinic's operating hours
 * (`hoursOfOperation`), timezone (extension), country (`address.country`),
 * emergency number and educational resources (extensions). Referenced from the
 * Organization via the clinic-location extension.
 */
export const buildClinicLocation = ({
  timezone,
  hours,
  country,
  emergencyNumber,
  educationalResources,
}: BuildClinicLocationParams): fhirR4.Location => {
  const extension: fhirR4.Extension[] = [buildClinicTimezoneExtension(timezone)];
  if (emergencyNumber) {
    extension.push({ url: CLINIC_EMERGENCY_NUMBER_EXTENSION_URL, valueString: emergencyNumber });
  }
  for (const resource of educationalResources ?? []) {
    extension.push(buildEducationalResourceExtension(resource));
  }

  const location: fhirR4.Location = {
    resourceType: "Location",
    id: CONTAINED_LOCATION_ID,
    status: fhirR4.Location.StatusEnum.Active,
    extension,
    hoursOfOperation: buildClinicHoursOfOperation(hours),
  };
  if (country) {
    location.address = { country };
  }

  return location;
};

/**
 * Builds the Organization extension that references the contained Location.
 */
export const buildClinicLocationExtension = (): fhirR4.Extension => {
  return {
    url: CLINIC_LOCATION_EXTENSION_URL,
    valueReference: { reference: `#${CONTAINED_LOCATION_ID}` },
  };
};

/**
 * Returns the clinic's contained Location resource, if present.
 */
export const getClinicLocation = (clinic: fhirR4.Organization): fhirR4.Location | undefined => {
  return clinic.contained?.find((resource): resource is fhirR4.Location => resource.resourceType === "Location");
};

/**
 * Reads the clinic timezone from its contained Location, falling back to the
 * default when not set.
 */
export const getClinicTimezone = (clinic: fhirR4.Organization): string => {
  const ext = getClinicLocation(clinic)?.extension?.find(e => e.url === CLINIC_TIMEZONE_EXTENSION_URL);
  return ext?.valueString || DEFAULT_CLINIC_TIMEZONE;
};

/**
 * Reads the clinic country (ISO alpha-2) from its contained Location's
 * address, or "" when not set.
 */
export const getClinicCountry = (clinic: fhirR4.Organization): string => {
  return getClinicLocation(clinic)?.address?.country || "";
};

/**
 * Reads the clinic emergency number from its contained Location, or "" when
 * not set (callers fall back to the patient-app default behaviour).
 */
export const getClinicEmergencyNumber = (clinic: fhirR4.Organization): string => {
  const ext = getClinicLocation(clinic)?.extension?.find(e => e.url === CLINIC_EMERGENCY_NUMBER_EXTENSION_URL);
  return ext?.valueString || "";
};

/**
 * Reads the clinic educational resources from its contained Location, in
 * stored order. Entries missing a label or url sub-extension are skipped.
 */
export const getClinicEducationalResources = (clinic: fhirR4.Organization): ClinicEducationalResource[] => {
  const extensions = getClinicLocation(clinic)?.extension ?? [];
  const resources: ClinicEducationalResource[] = [];
  for (const ext of extensions) {
    if (ext.url !== CLINIC_EDUCATIONAL_RESOURCE_EXTENSION_URL) {
      continue;
    }
    const label = ext.extension?.find(sub => sub.url === "label")?.valueString;
    const url = ext.extension?.find(sub => sub.url === "url")?.valueUrl;
    if (label && url) {
      resources.push({ label, url });
    }
  }
  return resources;
};

/**
 * Reads the clinic operating hours from its contained Location's
 * `hoursOfOperation`. Falls back to the default schedule (Mon–Fri 08:00–16:00)
 * when the clinic has no Location. Always returns seven entries ordered
 * Monday → Sunday; days absent from `hoursOfOperation` are treated as closed.
 */
export const getClinicHours = (clinic: fhirR4.Organization): ClinicDayHours[] => {
  const location = getClinicLocation(clinic);
  if (!location) {
    return getDefaultClinicHours();
  }

  // Start with every day closed, then open the days present in hoursOfOperation.
  const byDay = new Map<DayOfWeek, ClinicDayHours>(
    DAYS_OF_WEEK.map(day => [day, { day, open: false, start: DEFAULT_OPEN_START, end: DEFAULT_OPEN_END }])
  );

  for (const entry of location.hoursOfOperation ?? []) {
    for (const code of entry.daysOfWeek ?? []) {
      const day = code as DayOfWeek;
      if (DAYS_OF_WEEK.includes(day)) {
        byDay.set(day, {
          day,
          open: true,
          start: fromFhirTime(entry.openingTime),
          end: fromFhirTime(entry.closingTime),
        });
      }
    }
  }

  return DAYS_OF_WEEK.map(day => byDay.get(day)!);
};

/**
 * Creates a new clinic Organization resource. Operating hours and timezone are
 * stored on a contained Location (referenced via the clinic-location extension).
 */
export const createClinicOrganization = (
  name: string,
  phoneNumber: string,
  parentOrganizationId: string,
  primaryContactRef: fhirR4.Reference | undefined,
  locationParams: BuildClinicLocationParams
): fhirR4.Organization => {
  const extension: fhirR4.Extension[] = [];
  if (primaryContactRef) {
    extension.push({ url: PRIMARY_CONTACT_EXTENSION_URL, valueReference: primaryContactRef });
  }
  extension.push(buildClinicLocationExtension());

  const clinic: fhirR4.Organization = {
    resourceType: "Organization",
    active: true,
    name,
    telecom: [
      {
        system: fhirR4.ContactPoint.SystemEnum.Phone,
        value: phoneNumber,
        use: fhirR4.ContactPoint.UseEnum.Work,
        rank: 1,
      },
    ],
    partOf: {
      reference: formatters.formatOrganization(parentOrganizationId),
    },
    contained: [buildClinicLocation(locationParams)],
    extension,
  };

  return clinic;
};

/**
 * Gets the primary contact practitioner from a clinic
 */
export const getClinicPrimaryContact = (clinic: fhirR4.Organization): fhirR4.Reference | undefined => {
  return clinic.extension?.find(ext => ext.url === PRIMARY_CONTACT_EXTENSION_URL)?.valueReference;
};

/**
 * Extracts clinic information from Organization resource
 */
export const extractClinicInfo = (clinic: fhirR4.Organization) => {
  const phoneNumber = clinic.telecom?.find(t => t.system === "phone")?.value || "";
  const primaryContact = getClinicPrimaryContact(clinic);

  return {
    id: clinic.id,
    name: clinic.name || "",
    phoneNumber,
    active: clinic.active !== false,
    primaryContact,
    parentOrganization: clinic.partOf?.reference,
    timezone: getClinicTimezone(clinic),
    hours: getClinicHours(clinic),
    country: getClinicCountry(clinic),
    emergencyNumber: getClinicEmergencyNumber(clinic),
    educationalResources: getClinicEducationalResources(clinic),
  };
};
